import { useEffect, useMemo, useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { inventoryService } from "../../services";
import {
  formatAmount,
  formatLocationCountLabel,
  summarizeLocationCodes,
} from "../../utils/purchaseOrderPriceOptions";

const MANUAL_OPTION = "__manual__";

function LocationSummary({ codes, expanded, onToggle }) {
  const summary = summarizeLocationCodes(codes);

  if (summary.count === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {summary.previewText}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip
          label={formatLocationCountLabel(summary.count)}
          size="small"
          variant="outlined"
          sx={{ height: 22 }}
        />
        <Typography variant="caption" color="text.secondary">
          {summary.previewText}
        </Typography>
        {summary.hasMore ? (
          <Button
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              minWidth: 0,
              px: 0.5,
              py: 0,
              textTransform: "none",
              fontSize: "0.75rem",
            }}
          >
            {expanded ? "Hide" : "Show all"}
          </Button>
        ) : null}
      </Stack>
      <Collapse in={expanded && summary.hasMore}>
        <Box
          sx={{
            mt: 1,
            p: 1,
            maxHeight: 120,
            overflow: "auto",
            bgcolor: "grey.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {summary.all.map((code) => (
              <Chip key={code} label={code} size="small" sx={{ height: 22 }} />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

function PriceOptionRow({ option, selected, expanded, onToggleLocations }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        ml: -0.5,
        width: "100%",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.hover" : "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 3 }}
        sx={{ mb: 0.25 }}
      >
        <Typography variant="body2">
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ mr: 0.75 }}
          >
            Selling Price
          </Typography>
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {formatAmount(option.unit_cost)}
          </Typography>
        </Typography>
        <Typography variant="body2">
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ mr: 0.75 }}
          >
            MRP
          </Typography>
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {formatAmount(option.unit_price)}
          </Typography>
        </Typography>
      </Stack>
      <LocationSummary
        codes={option.location_codes}
        expanded={expanded}
        onToggle={onToggleLocations}
      />
    </Paper>
  );
}

function applyInitialSelection(grouped, product, initialPrices, setters) {
  const { setSelectedKey, setUnitCost, setUnitPrice } = setters;
  const initialCost =
    initialPrices?.unit_cost ??
    product?.unit_cost ??
    product?.unit_price ??
    "";
  const initialMrp = initialPrices?.unit_price ?? product?.unit_price ?? "";

  if (grouped.length > 0) {
    const matchIdx = grouped.findIndex(
      (group) =>
        Number(group.unit_cost) === Number(initialCost) &&
        Number(group.unit_price) === Number(initialMrp),
    );
    const idx = matchIdx >= 0 ? matchIdx : 0;
    setSelectedKey(String(idx));
    setUnitCost(
      grouped[idx].unit_cost != null ? String(grouped[idx].unit_cost) : "",
    );
    setUnitPrice(
      grouped[idx].unit_price != null ? String(grouped[idx].unit_price) : "",
    );
  } else {
    setSelectedKey(MANUAL_OPTION);
    setUnitCost(
      initialCost != null && initialCost !== "" ? String(initialCost) : "",
    );
    setUnitPrice(
      initialMrp != null && initialMrp !== "" ? String(initialMrp) : "",
    );
  }
}

export default function PurchaseOrderPriceSelectDialog({
  open,
  product,
  initialPrices = null,
  onClose,
  onConfirm,
}) {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [priceGroups, setPriceGroups] = useState([]);
  const [selectedKey, setSelectedKey] = useState(MANUAL_OPTION);
  const [expandedLocationKeys, setExpandedLocationKeys] = useState({});
  const [unitCost, setUnitCost] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [localError, setLocalError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const initializedRef = useRef(false);

  const sku = product?.sku || product?.article_code || "";

  useEffect(() => {
    if (!open) {
      setPage(0);
      initializedRef.current = false;
    }
  }, [open, sku]);

  useEffect(() => {
    if (!open || !sku) return undefined;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setExpandedLocationKeys({});

    inventoryService
      .getInventoryPriceOptions(sku, {
        page: page + 1,
        limit: rowsPerPage,
        group_by_price: true,
      })
      .then((res) => {
        if (cancelled) return;
        const options = res?.options || res?.data?.options || [];
        const grouped = options.map((option) => ({
          unit_cost:
            option.unit_cost != null && option.unit_cost !== ""
              ? Number(option.unit_cost)
              : null,
          unit_price:
            option.unit_price != null && option.unit_price !== ""
              ? Number(option.unit_price)
              : null,
          location_codes: Array.isArray(option.location_codes)
            ? option.location_codes
            : option.location_code
              ? [option.location_code]
              : [],
        }));
        setPriceGroups(grouped);
        setTotal(Number(res?.pagination?.total) || grouped.length);
        if (!initializedRef.current) {
          applyInitialSelection(grouped, product, initialPrices, {
            setSelectedKey,
            setUnitCost,
            setUnitPrice,
          });
          initializedRef.current = true;
        } else {
          // Keep entered prices; radio keys are page-local so clear selection.
          setSelectedKey(MANUAL_OPTION);
        }
        setLocalError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load price options",
        );
        setPriceGroups([]);
        setTotal(0);
        if (!initializedRef.current) {
          setSelectedKey(MANUAL_OPTION);
          const initialCost =
            initialPrices?.unit_cost ??
            product?.unit_cost ??
            product?.unit_price ??
            "";
          const initialMrp =
            initialPrices?.unit_price ?? product?.unit_price ?? "";
          setUnitCost(
            initialCost != null && initialCost !== "" ? String(initialCost) : "",
          );
          setUnitPrice(
            initialMrp != null && initialMrp !== "" ? String(initialMrp) : "",
          );
          initializedRef.current = true;
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // selectedKey intentionally omitted — only used for post-init page changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sku, product, initialPrices, page, rowsPerPage]);

  const groupedOptions = useMemo(
    () =>
      priceGroups.map((group, index) => ({
        key: String(index),
        unit_cost: group.unit_cost,
        unit_price: group.unit_price,
        location_codes: group.location_codes,
      })),
    [priceGroups],
  );

  const handleSelectChange = (value) => {
    setSelectedKey(value);
    setLocalError(null);
    if (value === MANUAL_OPTION) return;
    const selected = groupedOptions.find((option) => option.key === value);
    if (!selected) return;
    setUnitCost(
      selected.unit_cost != null ? String(selected.unit_cost) : "",
    );
    setUnitPrice(
      selected.unit_price != null ? String(selected.unit_price) : "",
    );
  };

  const toggleLocationExpand = (key) => {
    setExpandedLocationKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleConfirm = () => {
    const cost = Number(unitCost);
    const price = Number(unitPrice);
    if (!Number.isFinite(cost) || !(cost > 0)) {
      setLocalError("Selling Price is required and must be greater than 0.");
      return;
    }
    if (!Number.isFinite(price) || !(price > 0)) {
      setLocalError("MRP is required and must be greater than 0.");
      return;
    }
    if (cost > price) {
      setLocalError("Selling Price must not exceed MRP.");
      return;
    }
    setLocalError(null);
    onConfirm({ unit_cost: cost, unit_price: price });
  };

  const name = product?.product_name || "Product";
  const distinctPriceCount = total;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>Price config</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Pick a warehouse price tier or enter values manually. Prices apply to
          this purchase order only.
        </DialogContentText>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {name}
        </Typography>
        {sku ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 2 }}
          >
            Article / SKU: {sku}
          </Typography>
        ) : null}

        {loading && groupedOptions.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} sx={{ color: "#e90000" }} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ opacity: loading ? 0.7 : 1 }}>
            {fetchError ? (
              <Alert severity="warning">{fetchError}</Alert>
            ) : null}

            {groupedOptions.length > 0 || total > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Warehouse price tiers
                  {distinctPriceCount > 1
                    ? ` (${distinctPriceCount} distinct prices)`
                    : ""}
                </Typography>
                <RadioGroup
                  value={selectedKey}
                  onChange={(e) => handleSelectChange(e.target.value)}
                >
                  {groupedOptions.map((option) => (
                    <FormControlLabel
                      key={option.key}
                      value={option.key}
                      control={
                        <Radio
                          size="small"
                          sx={{ alignSelf: "flex-start", mt: 1.25 }}
                        />
                      }
                      sx={{
                        alignItems: "flex-start",
                        mx: 0,
                        mb: 1,
                        "& .MuiFormControlLabel-label": { width: "100%" },
                      }}
                      label={
                        <PriceOptionRow
                          option={option}
                          selected={selectedKey === option.key}
                          expanded={Boolean(expandedLocationKeys[option.key])}
                          onToggleLocations={() =>
                            toggleLocationExpand(option.key)
                          }
                        />
                      }
                    />
                  ))}
                  <FormControlLabel
                    value={MANUAL_OPTION}
                    control={<Radio size="small" />}
                    label="Enter manually"
                    sx={{ mx: 0, mt: 0.5 }}
                  />
                </RadioGroup>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10) || 10);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No warehouse prices found. Enter Selling Price and MRP below.
              </Typography>
            )}

            <TextField
              label="Selling Price *"
              type="number"
              value={unitCost}
              onChange={(e) => {
                setSelectedKey(MANUAL_OPTION);
                setUnitCost(e.target.value);
              }}
              inputProps={{ min: 0.01, step: "0.01" }}
              fullWidth
              required
            />
            <TextField
              label="MRP *"
              type="number"
              value={unitPrice}
              onChange={(e) => {
                setSelectedKey(MANUAL_OPTION);
                setUnitPrice(e.target.value);
              }}
              inputProps={{ min: 0.01, step: "0.01" }}
              fullWidth
              required
            />
          </Stack>
        )}

        {localError ? (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {localError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#c00000" } }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
